import React, { useMemo, useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCurrentPeriod } from "@/utility/helper";

const CYCLE_START_DAY = 21;
const MIN_SELECTABLE_YEAR = 1990;
const MAX_SELECTABLE_YEAR = new Date().getFullYear();

const formatDateISO = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const generateMonthOptionsForYear = (
  year: number,
  cycleStartDay: number = CYCLE_START_DAY,
) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0 to 11
  const currentDay = now.getDate();

  // Active month cycle: if today is 21st or later, current month cycle is available; otherwise up to previous month
  const activeCycleMonth = currentDay >= cycleStartDay ? currentMonth : currentMonth - 1;

  let maxMonth = 11;
  if (year > currentYear) {
    return [];
  } else if (year === currentYear) {
    maxMonth = activeCycleMonth;
  }

  const options: { label: string; startDate: string; endDate: string }[] = [];
  for (let m = 0; m <= maxMonth; m++) {
    const start = new Date(year, m, cycleStartDay);
    const end = new Date(year, m + 1, cycleStartDay - 1);
    options.push({
      label: `${start.toLocaleString("default", { month: "short" })} ${start.getFullYear()} - ${end.toLocaleString("default", { month: "short" })} ${end.getFullYear()}`,
      startDate: formatDateISO(start),
      endDate: formatDateISO(end),
    });
  }
  return options;
};

interface SalaryPeriodSelectorProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

export const SalaryPeriodSelector: React.FC<SalaryPeriodSelectorProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    if (startDate) {
      const parsed = new Date(startDate).getFullYear();
      if (!Number.isNaN(parsed)) return parsed;
    }
    return new Date(getCurrentPeriod().startDate).getFullYear();
  });

  // Ensure default period is set automatically based on current date (> 20 => 21st to 20th next month)
  useEffect(() => {
    if (!startDate || !endDate) {
      const current = getCurrentPeriod();
      onDateRangeChange(current.startDate, current.endDate);
    }
  }, [startDate, endDate, onDateRangeChange]);

  useEffect(() => {
    if (startDate) {
      const yr = new Date(startDate).getFullYear();
      if (!Number.isNaN(yr) && yr !== selectedYear) {
        setSelectedYear(yr);
      }
    }
  }, [startDate]);

  const monthOptions = useMemo(
    () => generateMonthOptionsForYear(selectedYear),
    [selectedYear],
  );

  const selectedPeriodLabel = useMemo(() => {
    const option = monthOptions.find(
      (opt) => opt.startDate === startDate && opt.endDate === endDate,
    );
    return option ? option.label : "Select period";
  }, [startDate, endDate, monthOptions]);

  const applyPeriod = (start: string, end: string) => {
    onDateRangeChange(start, end);
  };

  const handleYearChange = (value: string) => {
    const year = Number(value);
    if (!value || Number.isNaN(year)) return;
    if (year < MIN_SELECTABLE_YEAR || year > MAX_SELECTABLE_YEAR) return;

    const currentIdx = monthOptions.findIndex(
      (opt) => opt.startDate === startDate && opt.endDate === endDate,
    );
    setSelectedYear(year);
    const periods = generateMonthOptionsForYear(year);
    if (periods.length === 0) return;
    const next = (currentIdx >= 0 && periods[currentIdx]) || periods[periods.length - 1] || periods[0];
    if (next) {
      applyPeriod(next.startDate, next.endDate);
    }
  };

  const handlePeriodChange = (value: string) => {
    const option = monthOptions.find((opt) => opt.label === value);
    if (option) {
      applyPeriod(option.startDate, option.endDate);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">
        Salary Period:
      </span>

      <input
        type="number"
        inputMode="numeric"
        value={selectedYear}
        min={MIN_SELECTABLE_YEAR}
        max={MAX_SELECTABLE_YEAR}
        onChange={(e) => handleYearChange(e.target.value)}
        className="w-[82px] border border-slate-200 bg-white rounded-lg px-2.5 py-1 text-xs h-9 font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label="Salary year"
      />

      <Select
        value={selectedPeriodLabel}
        onValueChange={handlePeriodChange}
      >
        <SelectTrigger className="w-[195px] h-9 text-xs font-medium bg-white border-slate-200">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {monthOptions.map((opt) => (
            <SelectItem key={opt.label} value={opt.label} className="text-xs font-medium">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default SalaryPeriodSelector;
